import clsx from 'clsx';
import _ from 'lodash';
import React, { useCallback } from 'react';

import Badge from '@/src/components/common/Elements/Badge/Badge';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';

import { useInboxEntryPanel } from '../../../../store/inboxEntryPanel.store';
import {
  SidebarFileEntry,
  InboxEntry as InboxEntryType,
  InboxEntryStatus,
} from '../../../../types';
import useInboxSearchParams from '../../../../useInboxSearchParams';
import AttachmentItem from '../Attachments/AttachmentItem/AttachmentItem';
import InboxEntry, { mapSidebarInboxEntryCommonProps } from '../InboxEntry';
import StatusIcon from '../StatusIcons/StatusIcon/StatusIcon';

export const mapFileEntry = (rawInboxEntry): SidebarFileEntry => {
  return {
    file: {
      id: rawInboxEntry['source-id'],
      name: rawInboxEntry.subject,
      isFileSupported: !rawInboxEntry.document.name.startsWith('NOT_UPLOADED_'),
      isFromMessaging: Boolean(rawInboxEntry.document?.conversation),
    },
    googleDriveFileUrl: rawInboxEntry['google-drive-file-url'],
    status: _.upperFirst(
      rawInboxEntry.status.toLowerCase()
    ) as InboxEntryStatus,
    ...mapSidebarInboxEntryCommonProps(rawInboxEntry),
  };
};

export const getStatusIcons = (fileEntryStatus: string) => {
  return fileEntryStatus === InboxEntryStatus.Closed ? (
    <StatusIcon
      icon="close_file"
      svgProps={{ className: 'tw-fill-cfo-blue-400' }}
      containerClassName="tw-p-[5px] tw-mb-[3px]"
    />
  ) : (
    <StatusIcon
      icon="active_file"
      svgProps={{ className: 'tw-fill-cfo-green-300' }}
      containerClassName="tw-px-[17px] tw-py-[5px] tw-mb-[3px]"
      iconType={'rounded'}
      iconClassName="tw-mr-[5px]"
    >
      <span className={clsx('tw-text-cfo-black tw-font-brandonTextMedium')}>
        1
      </span>
    </StatusIcon>
  );
};

type FileEntryProps = {
  inboxEntry: InboxEntryType;
  // fileEntry: FileEntryType;
};

const FileEntry = ({ inboxEntry }: FileEntryProps) => {
  const fileEntry = inboxEntry.data as SidebarFileEntry;
  const { isTrash } = useInboxSearchParams();
  const showNewBadge = !isTrash && !fileEntry.isAccountRead;
  const header = (
    <LongTextByWidth
      className={clsx(
        'tw-text-[12px] tw-text-cfo-black',
        'tw-m-0',
        'tw-font-brandonTextMedium'
      )}
      id={`${fileEntry.id}`}
      value={fileEntry.from[0]}
      maxWidth={300}
      highlightBySearchTerm={true}
    />
  );
  const statusIcons = getStatusIcons(fileEntry.status);
  const { setInboxEntry } = useInboxEntryPanel();

  const handleClickCard = useCallback(() => {
    setInboxEntry(inboxEntry);
  }, [inboxEntry, setInboxEntry]);

  return (
    <React.Fragment>
      <InboxEntry
        inboxEntry={inboxEntry}
        date={fileEntry.createdDate}
        header={header}
        statusIcons={statusIcons}
        containerClassName={clsx('tw-flex tw-items-center')}
        onClick={handleClickCard}
      >
        <AttachmentItem file={fileEntry.file} containerClassName="tw-w-full">
          <LongTextByWidth
            id={`fn_${fileEntry.file.id}`}
            className={clsx(
              'tw-text-cfo-black tw-text-[12px]',
              'group-hover:tw-text-cfo-blue-600'
            )}
            value={fileEntry.file.name}
            maxWidth={275}
            maxLines={1}
            highlightBySearchTerm={true}
          />
        </AttachmentItem>

        {fileEntry.file.isFromMessaging && (
          <Icon
            icon={ICONS.message}
            className="tw-px-1 tw-text-cfo-blue-600 tw-text-[13px]"
            title="Uploaded from Conversations"
          />
        )}

        {fileEntry.googleDriveFileUrl && (
          <Icon
            icon={ICONS.gdrive({
              className: 'tw-text-cfo-grey-600',
              width: '13',
              height: '13',
            })}
          />
        )}
        {/* Display if newly added file entry */}
        {showNewBadge && (
          <Badge
            label="New"
            className={clsx(
              'tw-flex tw-items-center',
              'tw-rounded-[34px] tw-leading-[18px]',
              'tw-px-[10px] tw-py-0 tw-mx-[5px]',
              'tw-text-white tw-text-[10px] tw-font-brandonTextMedium',
              'tw-bg-cfo-blue-600'
            )}
          />
        )}
      </InboxEntry>
    </React.Fragment>
  );
};

export default FileEntry;
